import {
  importWooProducts,
  getWooSyncStatus,
} from "app/services/api/integrations";

/**
 * Dashboard "Dátová komunikácia" WooCommerce import handler.
 *
 * Starts an async WooCommerce import for the logged-in customer and polls the job
 * until it finishes, driving the shared import-progress modal. Sends the customer's
 * saved productsManagement config as an override so it works the same way as on the
 * customer detail page (no dependency on a server-side re-read).
 *
 * @param {Object} params
 * @param {Object} params.customer - logged-in customer (from localStorage "user")
 * @param {Function} params.setImportOpen
 * @param {Function} params.setImportProgress
 * @param {Function} params.enqueueSnackbar
 * @param {Function} params.t
 */
export const handleImportWoocommerce = async ({
  customer,
  setImportOpen,
  setImportProgress,
  enqueueSnackbar,
  t,
}) => {
  const customerId = customer?.customerId;
  const config = customer?.dsokSettings?.integrations?.productsManagement || null;

  if (!customerId) {
    enqueueSnackbar(
      t?.("pages.dsokCustomers.notifications.importFailed") || "Import zlyhal",
      { variant: "error" },
    );
    return;
  }

  setImportOpen(true);
  setImportProgress(0);

  // Indeterminate visual progress while the async job runs.
  let current = 0;
  const interval = setInterval(() => {
    current = Math.min(90, current + Math.floor(Math.random() * 6) + 2);
    setImportProgress((prev) => Math.max(prev ?? 0, current));
  }, 400);

  const finish = (ok, message) => {
    clearInterval(interval);
    setImportProgress(100);
    enqueueSnackbar(message, { variant: ok ? "success" : "error" });
    setTimeout(() => {
      setImportProgress(null);
      setImportOpen(false);
    }, 600);
  };

  try {
    const job = await importWooProducts(customerId, config);
    const jobId = job?.jobId;
    if (!jobId) throw new Error("Chýba jobId");

    const started = Date.now();
    const poll = async () => {
      let status;
      try {
        status = await getWooSyncStatus(jobId);
      } catch (e) {
        finish(false, `WooCommerce import zlyhal: ${e?.message || "chyba"}`);
        return;
      }

      const st = status?.status;
      if (st === "done") {
        const r = status?.progress?.result || {};
        finish(
          true,
          `WooCommerce import: ${r.productsActive ?? 0} produktov, ${
            r.categoriesUpserted ?? 0
          } kategórií`,
        );
        return;
      }
      if (st === "failed" || st === "canceled") {
        const msg = status?.error?.message || status?.progress?.message || st;
        finish(false, `WooCommerce import zlyhal: ${msg}`);
        return;
      }
      // still pending/running
      if (Date.now() - started > 5 * 60 * 1000) {
        finish(
          false,
          "WooCommerce import beží dlhšie ako 5 minút — pokračuje na pozadí.",
        );
        return;
      }
      setTimeout(poll, 2500);
    };
    setTimeout(poll, 1500);
  } catch (error) {
    const msg =
      error?.response?.data?.message ||
      error?.message ||
      "WooCommerce import zlyhal";
    finish(false, `WooCommerce import zlyhal: ${msg}`);
  }
};
